import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { TIERS, TIER_ORDER, hasTier } from "@/lib/tiers";
import { Lock, Sparkles, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";

const COPY = {
  en: {
    locked: "This feature needs a paid plan",
    subhead: "Pick the plan that matches how much support you need.",
    // Anonymous / signup pitch for Summarize
    signup_kicker: "100% Free · No credit card",
    signup_title: "Create your free account to break down court papers",
    signup_body: "Free accounts get 10 document explanations, our AI legal assistant, court-date reminders, and every learning tool. No credit card, no auto-renew.",
    signup_cta: "Sign up free",
    signup_skip: "Skip → start uploading",
    signin_link: "Already have an account? Sign in",
    signin_first: "Sign in to use your free uploads",
    loading: "Redirecting to checkout...",
    cancel_note: "Every plan is a one-time yearly charge. No auto-renew, no monthly bills.",
    used_up: "You've used all your document uploads on this plan. Upgrade for more.",
    free_left: (n, limit) => `You have ${n} of ${limit} uploads left on your current plan.`,
    unlock: (tier) => `Unlock ${tier}`,
    current: "Your current plan",
    compare: "See full plan comparison →",
    upgrade_more: "Need more than the free plan? Upgrade for more uploads and premium tools",
  },
  es: {
    locked: "Esta función necesita un plan de pago",
    subhead: "Elige el plan que se ajuste al soporte que necesitas.",
    signup_kicker: "100% Gratis · Sin tarjeta de crédito",
    signup_title: "Crea tu cuenta gratis para desglosar documentos de corte",
    signup_body: "Las cuentas gratuitas reciben 10 explicaciones de documentos, nuestro asistente legal con IA, recordatorios de corte y todas las herramientas de aprendizaje. Sin tarjeta, sin renovación automática.",
    signup_cta: "Registrarse gratis",
    signup_skip: "Saltar → empezar a subir",
    signin_link: "¿Ya tienes cuenta? Iniciar sesión",
    signin_first: "Inicia sesión para usar tus cargas gratis",
    loading: "Redirigiendo al pago...",
    cancel_note: "Cada plan es un cargo único anual. Sin renovación automática ni cargos mensuales.",
    used_up: "Usaste todas tus cargas de documentos en este plan. Sube de nivel para más.",
    free_left: (n, limit) => `Te quedan ${n} de ${limit} cargas en tu plan actual.`,
    unlock: (tier) => `Desbloquear ${tier}`,
    current: "Tu plan actual",
    compare: "Ver comparación completa →",
    upgrade_more: "¿Necesitas más que el plan gratuito? Sube de nivel para más cargas y herramientas premium",
  },
};

export function PaywallGate({ feature, minimum = "free", children }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const catalog = TIERS[lang] || TIERS.en;
  const [loading, setLoading] = useState(null); // stores lookup_key being loaded
  const [err, setErr] = useState("");

  const signedIn = user && typeof user === "object";
  const currentTier = signedIn ? (user.tier || "free") : "free";
  const uploadLimit = signedIn ? (user.limits?.uploads ?? user.free_limit ?? 10) : 10;
  const used = signedIn ? Number((user.usage || {})[feature] || 0) : uploadLimit;
  const isUnlimited = uploadLimit < 0;
  // Practice / Courtroom Coach is unlimited on every tier — signed-in users always pass through.
  // Any feature not in the quota-tracked set is treated as tier-only (no counter, no banner).
  const featureIsUnlimited = feature === "practice" || feature !== "summarize";
  // Practice and other free features don't require signin when `minimum === "free"`.
  const anonAllowed = !signedIn && minimum === "free" && featureIsUnlimited;
  const meetsMinTier = signedIn && hasTier(currentTier, minimum);
  const remaining = isUnlimited || featureIsUnlimited ? Infinity : Math.max(0, uploadLimit - used);
  // A user can access iff (a) they're a signed-in user meeting the min tier with quota,
  // or (b) the feature is unlimited AND requires only the free tier (no signin needed).
  const canUse = anonAllowed || (meetsMinTier && (isUnlimited || featureIsUnlimited || remaining > 0));

  // Signed-in users with quota (or unlimited feature): pass through with a helpful banner
  if (canUse) {
    const pct = uploadLimit > 0 ? Math.min(100, Math.round((used / uploadLimit) * 100)) : 0;
    const urgent = remaining <= 2;
    const empty = remaining === 0;
    return (
      <div>
        {signedIn && !isUnlimited && !featureIsUnlimited && (
          <div
            className={`max-w-4xl mx-auto mb-4 rounded-2xl border-2 px-4 py-3 ${
              urgent
                ? "bg-amber-50 border-amber-300"
                : "bg-[#FAF5EC] border-[#E4DDD3]"
            }`}
            data-testid={`quota-banner-${feature}`}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className={`w-4 h-4 ${urgent ? "text-amber-700" : "text-[#C85A32]"}`} />
                <span className={`font-semibold ${urgent ? "text-amber-900" : "text-[#1C2024]"}`}>
                  <span data-testid={`quota-remaining-${feature}`} className="tabular-nums font-extrabold">{remaining}</span>
                  {" "}{lang === "es" ? `de ${uploadLimit} cargas restantes` : `of ${uploadLimit} uploads left`}
                </span>
              </div>
              <Link
                to="/pricing"
                data-testid="quota-upgrade"
                className="pod-focus text-xs font-extrabold uppercase tracking-widest text-[#C85A32] hover:underline whitespace-nowrap"
              >
                {c.compare}
              </Link>
            </div>
            <div
              className="relative h-2 rounded-full bg-white overflow-hidden border border-[#E4DDD3]"
              role="progressbar"
              aria-valuenow={used}
              aria-valuemin={0}
              aria-valuemax={uploadLimit}
              aria-label={lang === "es" ? "Cargas usadas" : "Uploads used"}
              data-testid={`quota-progress-${feature}`}
            >
              <div
                className={`absolute inset-y-0 left-0 transition-all duration-500 ${
                  empty ? "bg-red-500" : urgent ? "bg-amber-500" : "bg-[#C85A32]"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
        {children}
      </div>
    );
  }

  const upgrade = async (lookup_key) => {
    if (!signedIn) return;
    setErr("");
    setLoading(lookup_key);
    try {
      const res = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Something went wrong. Please try again.");
      setLoading(null);
    }
  };

  // Which plans satisfy this paywall (must be >= minimum)
  const minIdx = TIER_ORDER.indexOf(minimum);
  const upgradeableTiers = TIER_ORDER.slice(minIdx).filter((t) => t !== "free");

  // Anonymous visitor hitting a Free-tier feature (like Summarize): show a signup
  // pitch that makes it clear the account is FREE, not "paid plan".
  if (!signedIn && minimum === "free") {
    const nextPath = typeof window !== "undefined" ? window.location.pathname : "/summarize";
    return (
      <div className="max-w-3xl mx-auto py-10 px-4" data-testid={`paywall-signup-${feature}`}>
        <div className="rounded-3xl border-2 border-[#C85A32] bg-white shadow-lg p-8 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-extrabold bg-emerald-50 text-emerald-800 border-2 border-emerald-300 px-3 py-1 rounded-full">
            <ShieldCheck className="w-3.5 h-3.5" /> {c.signup_kicker}
          </div>
          <h2 className="mt-4 text-2xl sm:text-3xl font-extrabold text-[#1C2024]">{c.signup_title}</h2>
          <p className="mt-3 text-[#4A525A] max-w-xl mx-auto">{c.signup_body}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to={`/register?next=${encodeURIComponent(nextPath)}`}
              data-testid="paywall-signup-cta"
              className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> {c.signup_cta}
            </Link>
            <Link
              to={`/login?next=${encodeURIComponent(nextPath)}`}
              data-testid="paywall-signin-link"
              className="pod-focus border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2"
            >
              {c.signin_link}
            </Link>
          </div>
          {/* Thumb-reach shortcut for hurried mobile users — same destination
              as the primary CTA but framed as a one-tap "just get me in there" */}
          <div className="mt-3">
            <Link
              to={`/register?next=${encodeURIComponent(nextPath)}`}
              data-testid="paywall-signup-skip"
              className="pod-focus text-sm font-semibold text-[#C85A32] hover:underline underline-offset-4"
            >
              {c.signup_skip}
            </Link>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-[#707880]">{c.upgrade_more}</p>
        <div className="mt-2 text-center">
          <Link to="/pricing" data-testid="paywall-compare-link" className="pod-focus text-sm font-semibold text-[#C85A32] hover:underline">
            {c.compare}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4" data-testid={`paywall-${feature}`}>
      <div className="text-center mb-8">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-[#C85A32] text-white flex items-center justify-center mb-4">
          <Lock className="w-7 h-7" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1C2024]">{c.locked}</h2>
        <p className="mt-2 text-[#4A525A]">{c.subhead}</p>
        {signedIn && used >= uploadLimit && !isUnlimited && (
          <div className="mt-4 max-w-lg mx-auto rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid={`quota-used-${feature}`}>
            {c.used_up}
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 ${upgradeableTiers.length >= 3 ? "md:grid-cols-3" : `md:grid-cols-${upgradeableTiers.length}`} gap-5`}>
        {upgradeableTiers.map((tKey) => {
          const t = catalog[tKey];
          const isCurrent = currentTier === tKey;
          const highlighted = tKey === "complete"; // visually emphasize middle plan
          return (
            <div
              key={tKey}
              data-testid={`plan-card-${tKey}`}
              className={`relative rounded-2xl border-2 p-6 flex flex-col ${
                highlighted
                  ? "border-[#C85A32] bg-[#FAF5EC] shadow-lg md:-translate-y-1"
                  : "border-[#E4DDD3] bg-white"
              }`}
            >
              {t.badge && (
                <span className={`absolute -top-2.5 right-4 text-[10px] font-extrabold uppercase tracking-widest text-white px-2.5 py-0.5 rounded-full ${
                  highlighted ? "bg-emerald-600" : "bg-emerald-500"
                }`}>
                  {t.badge}
                </span>
              )}
              <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">DIY Legal {t.name}</div>
              <div className="mt-2 flex items-baseline gap-1.5 flex-wrap">
                <span className="text-3xl font-extrabold text-[#1C2024]" data-testid={`plan-${tKey}-price`}>{t.price}</span>
                <span className="text-sm text-[#4A525A]">{t.period}</span>
              </div>
              <div className="mt-2 text-sm font-semibold text-[#C85A32]">{t.uploads_label}</div>
              <p className="mt-3 text-sm text-[#4A525A] leading-relaxed">{t.tagline}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-[#1C2024]">
                {t.features.slice(0, 5).map((f, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-[#C85A32] mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-5">
                {isCurrent ? (
                  <div className="text-center text-xs font-extrabold uppercase tracking-widest text-[#707880] py-3 border-2 border-dashed border-[#E4DDD3] rounded-xl">
                    {c.current}
                  </div>
                ) : signedIn ? (
                  <button
                    onClick={() => upgrade(t.lookup_key)}
                    disabled={loading !== null}
                    data-testid={`plan-${tKey}-upgrade`}
                    className={`pod-focus w-full font-bold py-3 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-70 ${
                      highlighted
                        ? "bg-[#C85A32] hover:bg-[#B24C28] text-white"
                        : "bg-[#1C2024] hover:bg-[#000] text-white"
                    }`}
                  >
                    {loading === t.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {loading === t.lookup_key ? c.loading : c.unlock(t.name)}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    data-testid={`plan-${tKey}-signin`}
                    className="pod-focus w-full text-center bg-[#1C2024] hover:bg-[#000] text-white font-bold py-3 rounded-xl inline-flex items-center justify-center"
                  >
                    {c.signin_link}
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {err && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 max-w-2xl mx-auto text-center" data-testid="paywall-error">{err}</div>}

      <p className="mt-6 text-center text-xs text-[#707880]">{c.cancel_note}</p>

      <div className="mt-3 text-center">
        <Link to="/pricing" data-testid="paywall-compare-link" className="pod-focus text-sm font-semibold text-[#C85A32] hover:underline">
          {c.compare}
        </Link>
      </div>
    </div>
  );
}
